import { useCallback } from 'react';
import { logInfo } from '@/services/observability';
import { useAdvertiserProfile } from '@/hooks/api/advertiser';
import type { TAdvertiserProfile } from '@/hooks/api/advertiser';
import { getQueryStringParameter, sanitizeString, buildTier } from './analytics.helpers';
import type {
	TAnalyticsDataLayer,
	TAnalyticsEvent,
	TCheckoutCartInput,
	TCheckoutConversionInput,
	TDataLayerCart,
	TDataLayerConversion,
	TDataLayerPage,
	TDataLayerProducts,
	TDataLayerSite,
	TDataLayerUser,
	TUpgradePlanInput,
	TUseAnalyticsReturn
} from './analytics.types';

const buildPage = (event: TAnalyticsEvent): TDataLayerPage => {
	const tierSegments = event.pageName.split('/').slice(event.tier * -1);
	return {
		pageName: event.pageName,
		pageType: event.pageType,
		flowType: event.flowType,
		trackingCode: {
			idcmp: getQueryStringParameter('idcmp'),
			idcmpint: getQueryStringParameter('idcmpint'),
			icid: getQueryStringParameter('icid'),
			lkid: getQueryStringParameter('lkid')
		},
		...buildTier(tierSegments),
		url: typeof window !== 'undefined' ? window.location.href : ''
	};
};

const buildSite = (): TDataLayerSite => ({
	brandOrigin: getQueryStringParameter('origem') ?? 'webmotors',
	domain: (typeof window !== 'undefined' && window.location.hostname) || 'webmotors.com.br',
	country: 'brasil',
	server: 'web',
	environment: 'servicos',
	subEnvironment: 'garagem',
	tmsVersion: 'S32',
	clientType: 'pf',
	platform: typeof window !== 'undefined' && window.screen.width <= 768 ? 'mobile' : 'desktop'
});

const buildUser = (profile: TAdvertiserProfile | null): TDataLayerUser | undefined => {
	if (!profile) return undefined;
	return {
		userID: String(profile.Id),
		loginStatus: '1',
		gender: profile.Sexo === 'F' ? '2' : '1',
		birthDate: profile.DataNascimento
			? new Date(profile.DataNascimento).toISOString().replace(/-/g, '/').substring(0, 10)
			: undefined,
		location: {
			city: sanitizeString(profile.Cidade),
			state: profile.Uf?.toLowerCase()
		}
	};
};

/**
 * Monta o bloco `products` a partir dos planos de upgrade — mesma transformação
 * da garagem (HOC `withAdobeLaunch`, branch `upgrade-plano`): o `name` é o 2º
 * termo do título sanitizado (ex.: "Plano Premium" → "premium").
 */
const buildProducts = (
	plans?: ReadonlyArray<TUpgradePlanInput>
): TDataLayerProducts | undefined => {
	if (!plans || plans.length === 0) return undefined;
	return {
		items: plans.map((plan) => ({
			sku: String(plan.sku),
			name: sanitizeString(typeof plan.name === 'string' ? plan.name.split(' ')[1] : undefined),
			originalPrice: plan.originalPrice,
			price: plan.price
		})),
		totalItems: String(plans.length),
		totalPrice: plans.reduce((sum, plan) => sum + parseFloat(String(plan.price)), 0).toFixed(2)
	};
};

const formatAnalyticsPrice = (value: number | string): string =>
	parseFloat(String(value)).toFixed(2);

const extractPlanCartName = (name: string): string | undefined => {
	const sanitized = sanitizeString(name.replace(/^Plano\s+/i, '').split(' ')[0]);
	return sanitized;
};

/**
 * Monta o bloco `cart` a partir do item do pedido — paridade garagem upgrade.
 */
const buildCart = (cart?: TCheckoutCartInput): TDataLayerCart | undefined => {
	if (!cart) return undefined;

	const price = formatAnalyticsPrice(cart.price);

	return {
		totalItems: '1',
		totalPrice: price,
		items: [
			{
				sku: String(cart.sku),
				name: extractPlanCartName(cart.name),
				originalPrice: formatAnalyticsPrice(cart.originalPrice),
				price
			}
		]
	};
};

const buildConversion = (
	conversion?: TCheckoutConversionInput,
	cart?: TDataLayerCart
): TDataLayerConversion | undefined => {
	if (!conversion || !cart) return undefined;

	const privateSeller: TDataLayerConversion['privateSeller'] = {
		transactionID: conversion.transactionID,
		transactionTotal: cart.totalPrice,
		paymentType: conversion.paymentType
	};

	if (conversion.creditCardFlag) {
		privateSeller.creditCardFlag = conversion.creditCardFlag;
	}

	return { privateSeller };
};

/**
 * Monta o objDataLayer aninhado respeitando o array `use` do evento — mesma
 * mecânica do HOC `withAdobeLaunch` da garagem: cada escopo liga um bloco.
 */
const buildDataLayer = (
	event: TAnalyticsEvent,
	profile: TAdvertiserProfile | null
): TAnalyticsDataLayer => {
	const dataLayer: TAnalyticsDataLayer = {};

	event.use.forEach((scope) => {
		switch (scope) {
			case 'page':
				dataLayer.page = buildPage(event);
				break;
			case 'site':
				dataLayer.site = buildSite();
				break;
			case 'user': {
				const user = buildUser(profile);
				if (user) dataLayer.user = user;
				break;
			}
			case 'ad':
				if (event.ad) dataLayer.ad = event.ad;
				break;
			case 'products': {
				const products = buildProducts(event.upgradePlans);
				if (products) dataLayer.products = products;
				break;
			}
			case 'cart': {
				const cart = buildCart(event.checkoutCart);
				if (cart) dataLayer.cart = cart;
				break;
			}
			case 'conversion': {
				const cart = dataLayer.cart ?? buildCart(event.checkoutCart);
				const conversion = buildConversion(event.checkoutConversion, cart);
				if (conversion) dataLayer.conversion = conversion;
				break;
			}
		}
	});

	return dataLayer;
};

/**
 * Analytics hook — despacha CustomEvents para o Adobe Data Layer com a mesma
 * estrutura aninhada (page/site/user/ad) da garagem.
 *
 * Fluxo:
 * 1. Lê o perfil do anunciante (cacheado) para o bloco `user`
 * 2. Monta o objDataLayer a partir do `use` do evento
 * 3. Seta window.objDataLayer (Adobe Launch lê dessa global)
 * 4. Despacha um CustomEvent no document (Adobe Launch escuta)
 * 5. Loga o evento no Datadog para observabilidade
 *
 * Uso:
 *   const { trackPageView, trackClick, trackEvent } = useAnalytics();
 *   trackPageView({ pageName: '/webmotors/garagem/meus-anuncios/ativos', use: ['site','user','page'], tier: 3, ... });
 */
const useAnalytics = (): TUseAnalyticsReturn => {
	const { profile, isLoading: isProfileLoading } = useAdvertiserProfile();

	const dispatch = useCallback(
		(eventName: string, event: TAnalyticsEvent): void => {
			const dataLayer = buildDataLayer(event, profile);

			window.objDataLayer = dataLayer as unknown as Record<string, unknown>;
			document.dispatchEvent(new CustomEvent(eventName, { detail: dataLayer }));

			logInfo(`[analytics] ${eventName}`, {
				pageName: event.pageName,
				pageType: event.pageType,
				flowType: event.flowType,
				tier: String(event.tier)
			});
		},
		[profile]
	);

	const trackPageView = useCallback(
		(event: TAnalyticsEvent): void => {
			dispatch('customPageView', event);
		},
		[dispatch]
	);

	const trackClick = useCallback(
		(event: TAnalyticsEvent): void => {
			dispatch(event.eventName ?? 'customClick', event);
		},
		[dispatch]
	);

	const trackEvent = useCallback(
		(eventName: string, event: TAnalyticsEvent): void => {
			dispatch(eventName, event);
		},
		[dispatch]
	);

	return { trackPageView, trackClick, trackEvent, isProfileReady: !isProfileLoading };
};

export { useAnalytics };
